import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { LibraryItem } from '@shared/types.ts';
import { player } from '@/lib/player.ts';
import { useUi } from '@/stores/ui.ts';
import { isBrowsable } from './Poster.tsx';

/**
 * Pressing a tile: play it, or open it.
 *
 * One hook rather than the same twelve lines on three screens — and more to
 * the point, one place where "presentation moves first" is decided. A
 * transcode can take a moment to start and the screen has no reason to sit
 * still while it does; that reasoning is identical to tuning a channel and
 * should not be re-derived per screen.
 */
export function usePlayItem() {
  const navigate = useNavigate();
  const setView = useUi((s) => s.setView);

  const play = useCallback(async (item: LibraryItem) => {
    setView('theater');
    try {
      await player.playItem(item);
    } catch {
      setView('browse');   // the error is in the player's own state
    }
  }, [setView]);

  const open = useCallback((item: LibraryItem) => {
    if (isBrowsable(item)) navigate(`/library/item/${encodeURIComponent(item.id)}`);
    else void play(item);
  }, [navigate, play]);

  return { play, open };
}
